'use client'

import React, { useEffect, useRef } from 'react'
import gsap from 'gsap'
import ExperiencedManagement from './ExperiencedManagement'
import BuildingforFuture from './Building for Future'
import { useLanguage } from '../../providers/LanguageProvider'
import clsx from 'clsx'
import HLSPlayer from '../Common/HLSPlayer'
const WhyChooseSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.why-heading', {
        y: 30,
        opacity: 0,
        duration: 0.3,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
          once: true,
        },
      })

      gsap.from('.why-paragraph', {
        y: 30,
        opacity: 0,
        duration: 0.3,
        delay: 0.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          once: true,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      className="bg-black w-full py-12 sm:py-16 lg:pt-[95px] lg:pb-[100px] 2xl:py-36"
    >
      <div className="section-container flex flex-col mx-auto">
        {/* Header */}
        <div className="sm:text-center w-full mb-12 sm:mb-14 md:mb-[80px]">
          <h1
            className={clsx(
              'text-white font-[500] leading-snug why-heading',
              isArabic
                ? 'text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px] 2xl:text-[42px]'
                : 'text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px] 2xl:text-[42px]',
            )}
          >
            {isArabic ? 'لماذا تختار كونتينر سيتي' : 'Why Choose Container City'}
            {!isArabic && (
              <span className="ml-1 text-[22px] sm:text-[26px] md:text-[32px] xl:text-[38px] 2xl:text-[42px]">
                ®
              </span>
            )}
          </h1>

          <p className="mt-5 sm:mt-9 text-white font-light xl:w-[68%] 2xl:w-[70%] leading-5 text-[15px] sm:text-[15px] md:text-[15px] 2xl:text-lg max-w-5xl mx-auto">
            {isArabic
              ? `مصمم عم عضو اللجنة في متابعة الرؤية الوطنية لقطر 2030، بدعم من CTC ومن خلال مجموعة موارد وCCSM تم إعدادك حسب احتياجاتك. بهدف بناء بنية تحتية موثوقة من خلال جميع الجوانب الحيوية.`
              : 'Built with scalability in mind and aligned with Qatar National Vision 2030, Container City® is designed to grow with your needs. Backed by the Aero Group and CTC, we bring decades of trust and infrastructure to every container we manage.'}
          </p>
        </div>

        {/* Grid Row 1 – Videos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-3 lg:gap-4 xl:gap-4 mb-4 md:mb-3 lg:mb-4 xl:mb-4">
          <div className="relative h-[250px] sm:h-[300px] md:h-[320px] xl:h-[342px] 2xl:h-[400px] overflow-hidden rounded-md">
            <video
              src="https://d3lvumh1qhmw7p.cloudfront.net/government.webm" ///videos/government.webm
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              aria-label="Aerial view of Qatar's government-backed container depot, showcasing rows of cargo containers along a busy waterfront."
            />

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/40 z-10 flex items-end px-3 py-3 sm:px-4 sm:py-4 xl:p-6">
              <div className="text-white">
                <h2 className="text-[18px] sm:text-[22px] md:text-[26px] xl:text-[36px] font-medium mb-2 sm:mb-3 xl:mb-4 leading-snug">
                  {isArabic ? (
                    'مدعوم من الحكومة'
                  ) : (
                    <>
                      Government <br /> Backed
                    </>
                  )}
                </h2>
                <p className="text-[13px] sm:text-sm md:text-[15px] 2xl:text-lg text-gray-200 font-light leading-relaxed">
                  {isArabic ? (
                    'أول مستودع حاويات مرخص بالكامل...'
                  ) : (
                    <>
                      Qatar’s first fully legalized container <br />
                      depot with full regulatory compliance
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Video Block 2 */}
          <div className="relative h-[250px] sm:h-[300px] md:h-[320px] xl:h-[342px] 2xl:h-[400px] overflow-hidden rounded-md">
            <video
              src="https://d3lvumh1qhmw7p.cloudfront.net/strategic-location.webm" ///videos/strategic-location.webm
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              aria-label="Image of a cargo ship navigating near a city bridge, highlighting its strategic location close to Hamad Port and Doha."
            />

            {/* Overlay layer */}
            <div className="absolute inset-0 bg-black/40 z-10 flex items-end px-3 py-3 sm:px-4 sm:py-4 xl:p-6">
              <div className="text-white z-10">
                <h2 className="text-[18px] sm:text-[22px] md:text-[26px] xl:text-[36px] font-medium mb-2 sm:mb-3 xl:mb-4 leading-snug">
                  {isArabic ? (
                    'الموقع الاستراتيجي'
                  ) : (
                    <>
                      Strategic <br /> Location
                    </>
                  )}
                </h2>
                <p className="text-[13px] sm:text-sm md:text-[15px] 2xl:text-lg text-gray-200 font-light leading-relaxed">
                  {isArabic ? (
                    '12 كم من ميناء حمد، 40 كم من الدوحة...'
                  ) : (
                    <>
                      12km from Hamad Port, 40km from <br /> Doha, near Mesaieed Industrial city
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Grid Row 2 – Features (4 blocks) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-[2.5fr_1.31fr_2.5fr_1.31fr] gap-4 md:gap-3 lg:gap-4 xl:gap-4">
          {/* Block 1 – Advanced Security */}
          <div className="relative order-1 md:order-1 col-span-1 sm:col-span-2 md:col-span-1 h-[220px] sm:h-[250px] xl:h-[286px] 2xl:h-[330px] overflow-hidden">
            <img
              src="https://d3lvumh1qhmw7p.cloudfront.net/advanced-security.jpg" ///images/home/advanced-security.jpg
              alt="Aerial view of a container ship navigating the ocean, showcasing colorful cargo containers, with text about advanced security services. "
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end p-2.5 sm:p-5 xl:p-6 ">
              <div className="text-white">
                <h3 className="text-[16px] sm:text-[22px] xl:text-[24px] 2xl:text-[26px] font-semibold mb-2 2xl:mb-5 leading-6 sm:leading-8">
                  {isArabic ? (
                    'أمن متقدم'
                  ) : (
                    <>
                      Advanced <br /> Security
                    </>
                  )}
                </h3>
                <p className="text-[13px] sm:text-sm 2xl:text-[18px] xl:w-80 font-light text-[#F8F8F8]">
                  {isArabic ? (
                    'مراقبة 24/7، وصول مُراقَب، وإشراف مهني'
                  ) : (
                    <>24/7 monitoring, controlled access, and professional oversight</>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Block 2 – Experienced Management (Responsive ✅) */}
          <ExperiencedManagement />

          {/* Block 3 – Comprehensive Solutions */}
          <div className="relative order-4 sm:order-4 md:order-3 h-[220px] col-span-1 sm:col-span-2  md:col-span-1 sm:h-[250px] xl:h-[286px] 2xl:h-[330px] overflow-hidden">
            <img
              src="https://d3lvumh1qhmw7p.cloudfront.net/comprehensive-solutions.jpg" ///images/home/comprehensive-solutions.jpg
              alt="Aerial view of a truck navigating through rows of colorful shipping containers, highlighting container storage and logistics services. "
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end p-2.5 sm:p-5 xl:p-6">
              <div className="text-white ">
                <h3 className="text-[16px] sm:text-[22px] xl:text-[24px] 2xl:text-[26px] font-semibold mb-2 2xl:mb-5 leading-6 sm:leading-8">
                  {isArabic ? (
                    'حلول شاملة'
                  ) : (
                    <>
                      Comprehensive <br /> Solutions
                    </>
                  )}
                </h3>
                <p className="text-[13px] sm:text-sm 2xl:text-[18px] xl:w-80 font-light text-[#F8F8F8]">
                  {isArabic ? (
                    'تخزين الحاويات، التخزين الذاتي...'
                  ) : (
                    <>Container storage, self-storage, depot operations, and logistics services</>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Block 4 – Building for Future (Responsive ✅) */}
          <BuildingforFuture />
        </div>
      </div>
    </section>
  )
}

export default WhyChooseSection
